import React, { useState, useRef, useCallback } from 'react';
import { MoreVertical, Ban, ThumbsDown, Loader2, ArrowLeftRight } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

/**
 * ExerciseActionMenu - Provides exercise feedback actions via 3-dot menu
 * 
 * Works in two modes:
 * 1. With workout context (workoutPlanId provided): Replaces exercise in current plan
 * 2. Without workout context: Just saves preference for future workouts
 * 
 * Actions:
 * - No access to this equipment (permanent equipment block)
 * - I don't like this exercise (permanent exercise block)
 */
const ExerciseActionMenu = ({ 
  exercise,
  workoutPlanId,
  dayIndex,
  exerciseIndex,
  onExerciseReplaced,
  onPreferenceUpdated,
  onReplaceWithPicker,
  disabled = false,
  className = ""
}) => {
  const { api } = useAuth();
  const [confirmDialog, setConfirmDialog] = useState(null); // 'no_equipment' | 'dislike' | null
  const [isProcessing, setIsProcessing] = useState(false);

  // Check if we're in workout context mode or preference-only mode
  const hasWorkoutContext = workoutPlanId !== undefined && dayIndex !== undefined && exerciseIndex !== undefined;

  const exerciseName = exercise?.exercise || exercise?.name || 'this exercise';
  const equipmentType = exercise?.equipment || exercise?.equipment_type || 'this equipment';

  const handleAction = async (reason) => {
    setIsProcessing(true);
    setConfirmDialog(null);

    try {
      if (hasWorkoutContext) {
        // Mode 1: Replace exercise in workout
        const response = await api.post('/workouts/replace-exercise', {
          workout_plan_id: workoutPlanId,
          day_index: dayIndex,
          exercise_index: exerciseIndex,
          reason: reason
        });

        if (response.data.replaced) {
          toast.success(response.data.message || "Exercise replaced successfully!", {
            duration: 3000,
          });

          if (onExerciseReplaced) {
            onExerciseReplaced({
              oldExercise: response.data.old_exercise,
              newExercise: response.data.new_exercise,
              updatedDay: response.data.updated_day,
              dayIndex: dayIndex,
              exerciseIndex: exerciseIndex
            });
          }
        } else {
          if (response.data.suggest_regeneration) {
            toast.error(response.data.error || "Too many swaps — please regenerate the workout.", {
              duration: 4000,
            });
          } else {
            toast.error(response.data.error || "No suitable alternative found.", {
              duration: 4000,
            });
          }
        }
      } else {
        // Mode 2: Just save preference (no workout replacement)
        if (reason === 'dislike') {
          const response = await api.post('/user/preferences/block-exercise', {
            exercise_id: exercise?._id || exercise?.id,
            exercise_name: exerciseName
          });

          if (response.data.success) {
            toast.success(`Got it — "${exerciseName}" won't appear in your workouts.`, {
              duration: 3000,
            });
            onPreferenceUpdated?.({ type: 'dislike', exercise: exerciseName });
          } else {
            toast.error(response.data.error || "Failed to update preferences.", {
              duration: 4000,
            });
          }
        } else if (reason === 'no_equipment') {
          const response = await api.post('/user/preferences/block-equipment', {
            equipment_type: equipmentType
          });

          if (response.data.success) {
            toast.success(`Got it — exercises requiring "${equipmentType}" won't appear in your workouts.`, {
              duration: 3000,
            });
            onPreferenceUpdated?.({ type: 'no_equipment', equipment: equipmentType });
          } else {
            toast.error(response.data.error || "Failed to update preferences.", {
              duration: 4000,
            });
          }
        }
      }
    } catch (error) {
      console.error('Failed to process action:', error);
      toast.error(error.response?.data?.detail || "Failed to process. Please try again.", {
        duration: 4000,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const getConfirmationContent = () => {
    if (confirmDialog === 'no_equipment') {
      return {
        title: "Block This Equipment?",
        description: hasWorkoutContext
          ? `This will permanently hide all "${equipmentType}" exercises from your workouts. A suitable alternative will be selected immediately.`
          : `Exercises requiring "${equipmentType}" will be excluded from your future workouts. You can change this anytime in Settings.`,
      };
    }
    if (confirmDialog === 'dislike') {
      return {
        title: "Block This Exercise?",
        description: hasWorkoutContext
          ? `"${exerciseName}" will be permanently hidden from your workouts. A suitable alternative will be selected immediately.`
          : `"${exerciseName}" will be excluded from your future workouts. You can change this anytime in Settings.`,
      };
    }
    return { title: "", description: "" };
  };

  const dialogContent = getConfirmationContent();

  // Scroll-safe tap: only open menu if finger stays still for the entire touch
  const [menuOpen, setMenuOpen] = useState(false);
  const startPos = useRef(null);

  const handleTouchStart = useCallback((e) => {
    const t = e.touches[0];
    startPos.current = { x: t.clientX, y: t.clientY };
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (!startPos.current) return;
    const t = e.changedTouches[0];
    const dx = Math.abs(t.clientX - startPos.current.x);
    const dy = Math.abs(t.clientY - startPos.current.y);
    startPos.current = null;
    // Only open if finger barely moved — NOT a scroll
    if (dx < 4 && dy < 4) {
      setMenuOpen(true);
    }
  }, []);

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        {/* Invisible anchor for dropdown positioning */}
        <DropdownMenuTrigger asChild>
          <div className="shrink-0" />
        </DropdownMenuTrigger>

        {/* Visible button — completely decoupled from Radix trigger */}
        <div
          role="button"
          tabIndex={0}
          className={`h-8 w-8 rounded-full bg-muted/40 border-2 border-accent/40 text-muted-foreground hover:text-foreground hover:border-accent/70 hover:bg-muted transition-all flex items-center justify-center shrink-0 select-none ${className} ${(disabled || isProcessing) ? 'opacity-50 pointer-events-none' : ''}`}
          data-testid={hasWorkoutContext ? `exercise-menu-${exerciseIndex}` : 'exercise-menu'}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onClick={(e) => {
            // Desktop only — touch devices use touchStart/End above
            if (!('ontouchstart' in window)) {
              setMenuOpen(true);
            }
          }}
          onKeyDown={(e) => { if (e.key === 'Enter') setMenuOpen(true); }}
        >
          {isProcessing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <MoreVertical className="h-4 w-4" />
          )}
        </div>
        <DropdownMenuContent align="end" className="w-64 p-2 rounded-xl border-border">
          {hasWorkoutContext && onReplaceWithPicker && (
            <DropdownMenuItem 
              onClick={() => onReplaceWithPicker(dayIndex, exerciseIndex)}
              className="cursor-pointer rounded-lg h-12 px-3 gap-3"
              data-testid={`exercise-replace-picker-${exerciseIndex}`}
            >
              <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                <ArrowLeftRight className="h-4 w-4 text-accent" />
              </div>
              <span className="text-sm font-semibold">Replace with...</span>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem 
            onClick={() => setConfirmDialog('no_equipment')}
            className="cursor-pointer rounded-lg h-12 px-3 gap-3"
            data-testid={hasWorkoutContext ? `exercise-no-equipment-${exerciseIndex}` : 'exercise-no-equipment'}
          >
            <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center shrink-0">
              <Ban className="h-4 w-4 text-orange-400" />
            </div>
            <span className="text-sm font-semibold">No access to equipment</span>
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => setConfirmDialog('dislike')}
            className="cursor-pointer rounded-lg h-12 px-3 gap-3"
            data-testid={hasWorkoutContext ? `exercise-dislike-${exerciseIndex}` : 'exercise-dislike'}
          >
            <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center shrink-0">
              <ThumbsDown className="h-4 w-4 text-red-400" />
            </div>
            <span className="text-sm font-semibold">I don't like this</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Confirmation Dialog */}
      <AlertDialog open={!!confirmDialog} onOpenChange={() => setConfirmDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{dialogContent.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {dialogContent.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="exercise-action-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleAction(confirmDialog)}
              className="bg-accent text-accent-foreground hover:bg-accent/90"
              data-testid="exercise-action-confirm"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {hasWorkoutContext ? 'Replacing...' : 'Saving...'}
                </>
              ) : (
                'Confirm'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ExerciseActionMenu;
